import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  InputAdornment,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import useNotification from "../common/Notification";
import RegisterUserModal from "./RegisterUserModal";
import UserServicesModal from "./UserServicesModal";
import { openUserServiceInNewTab, USER_SERVICE_SLUGS } from "./adminServiceConfig";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import {
  formatRoleChips,
  fetchUsersForSection,
  SECTION_META,
} from "./adminUtils";

const SECTION_SERVICE_SLUG = {
  personal: USER_SERVICE_SLUGS.personalWhatsapp,
  email: USER_SERVICE_SLUGS.email,
  "whatsapp-business": USER_SERVICE_SLUGS.whatsappBusiness,
};

const ROLE_CHIP_COLORS = {
  PERSONAL: { bg: "rgba(37, 99, 235, 0.1)", color: "#1d4ed8" },
  EMAIL: { bg: "rgba(124, 58, 237, 0.1)", color: "#6d28d9" },
  "WB-ADMIN": { bg: "rgba(16, 185, 129, 0.12)", color: "#047857" },
  "WB-USER": { bg: "rgba(5, 150, 105, 0.1)", color: "#065f46" },
  "WB-AGENT": { bg: "rgba(234, 88, 12, 0.1)", color: "#c2410c" },
  "WB-CHAT": { bg: "rgba(14, 165, 233, 0.1)", color: "#0369a1" },
  "CRM-ADMIN": { bg: "rgba(219, 39, 119, 0.1)", color: "#be185d" },
};

const AdminUserList = ({ section = "all" }) => {
  const meta = SECTION_META[section] || SECTION_META.all;
  const notify = useNotification();
  const isServerPaginated = section === "all" || section === "personal" || section === "email";

  const [users, setUsers] = useState([]);
  const [metaInfo, setMetaInfo] = useState({ total: 0, last_page: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [openCreate, setOpenCreate] = useState(false);
  const [servicesModalOpen, setServicesModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { users: data, meta: m } = await fetchUsersForSection(
        section,
        debouncedSearch,
        page + 1,
        rowsPerPage
      );
      setUsers(data);
      setMetaInfo({
        total: m.total ?? data.length,
        last_page: m.last_page ?? 1,
      });
      setSelectedUser((prev) => {
        if (!prev?.id) return prev;
        return data.find((u) => Number(u.id) === Number(prev.id)) || prev;
      });
    } catch (err) {
      const msg = err?.message || "Failed to load users";
      setError(msg);
      notify(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [section, debouncedSearch, page, rowsPerPage, notify]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    setPage(0);
  }, [section, debouncedSearch]);

  const totalCount = isServerPaginated ? metaInfo.total : users.length;

  const displayUsers = useMemo(() => {
    if (isServerPaginated) return users;
    const start = page * rowsPerPage;
    return users.slice(start, start + rowsPerPage);
  }, [users, page, rowsPerPage, isServerPaginated]);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderRoleChips = (userRoleStr) => {
    const chips = formatRoleChips(userRoleStr);
    if (chips.length === 0) {
      return <Typography variant="caption" color="text.secondary">—</Typography>;
    }
    return (
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {chips.map(({ token, label }) => {
          const palette = ROLE_CHIP_COLORS[token] || { bg: "rgba(0,0,0,0.06)", color: "text.primary" };
          return (
            <Chip key={token} label={label} size="small" sx={{ fontWeight: 600, fontSize: "0.72rem", height: 24, bgcolor: palette.bg, color: palette.color, border: "none" }} />
          );
        })}
      </Box>
    );
  };

  const renderCredits = (user) => {
    if (section === "personal") {
      return `PWC ${user.pwc ?? 0} · DWPC ${user.dwpc ?? 0}`;
    }
    if (section === "email") {
      return `BEC ${user.bec ?? 0} · DBEC ${user.dbec ?? 0}`;
    }
    if (section === "whatsapp-business") {
      return `BWC ${user.bwc ?? 0}${user.dbwc != null ? ` · DBWC ${user.dbwc}` : ""}`;
    }
    const parts = [];
    if (user.pwc != null) parts.push(`PWC ${user.pwc}`);
    if (user.bec != null) parts.push(`BEC ${user.bec}`);
    if (user.bwc != null) parts.push(`BWC ${user.bwc}`);
    return parts.length ? parts.join(" · ") : "—";
  };

  const openUserDetail = (user) => {
    if (section === "all") {
      setSelectedUser(user);
      setServicesModalOpen(true);
      return;
    }
    const slug = SECTION_SERVICE_SLUG[section];
    if (slug) {
      openUserServiceInNewTab(user.id, slug);
    }
  };

  const tableHeaders = ["ID", "Name", "Email", "Roles", "Credits", "Status", "Created", ""];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ ...WB_SX.headerRow, mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ ...WB_SX.pageTitle, borderLeft: `4px solid ${meta.accent}`, pl: 1.5 }}>
            {meta.title}
          </Typography>
          <Typography variant="body2" sx={{ ...WB_SX.pageSubtitle, maxWidth: 720, mt: 0.5, lineHeight: 1.65 }}>
            {meta.subtitle}
            {section === "all"
              ? " Click a user to see their services, then open any service in a new tab."
              : " Click a user to open that service editor in a new tab."}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={loadUsers} disabled={loading} sx={WB_SX.card}><RefreshIcon /></IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenCreate(true)} sx={WB_SX.primaryButton}>
            Create user
          </Button>
        </Box>
      </Box>

      {error ? <Alert severity="warning" sx={{ mb: 2, borderRadius: WB_UI.radius.card }}>{error}</Alert> : null}

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by ID, name, or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.secondary", fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ maxWidth: 480, ...WB_SX.card, bgcolor: WB_UI.color.cardBgStrong, "& .MuiOutlinedInput-root": { borderRadius: WB_UI.radius.card } }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {totalCount} user{totalCount === 1 ? "" : "s"}
        </Typography>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ ...WB_SX.card, bgcolor: WB_UI.color.cardBgStrong, overflow: "auto" }}>
        <Table size="small" sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {tableHeaders.map((h) => (
                <TableCell key={h} sx={WB_SX.tableHeadCell}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 5 }}>
                  <CircularProgress size={28} sx={{ color: WB_UI.color.primaryButtonBg }} />
                </TableCell>
              </TableRow>
            ) : displayUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableHeaders.length} align="center" sx={{ py: 5 }}>
                  <Typography variant="body2" color="text.secondary">No users found in this section.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayUsers.map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  sx={{ ...WB_SX.tableRow, cursor: "pointer", "&:hover": { bgcolor: "rgba(37, 99, 235, 0.04)" } }}
                  onClick={() => openUserDetail(user)}
                >
                  <TableCell sx={{ fontWeight: 600 }}>{user.id}</TableCell>
                  <TableCell>{user.name || "—"}</TableCell>
                  <TableCell>{user.email || "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>{renderRoleChips(user.user_role)}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{renderCredits(user)}</Typography>
                  </TableCell>
                  <TableCell>
                    {user.account_disabled ? (
                      <Chip label="Disabled" size="small" color="error" variant="outlined" />
                    ) : (
                      <Chip label="Active" size="small" color="success" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>{formatDate(user.created_at)}</TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title={section === "all" ? "View services" : "Open service editor"}>
                      <IconButton size="small" onClick={() => openUserDetail(user)} sx={{ color: WB_UI.color.primaryButtonBg }}>
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 15, 25, 50, 100]}
      />

      <RegisterUserModal open={openCreate} onClose={() => setOpenCreate(false)} onUserAdded={loadUsers} defaultRoles={meta.defaultRoles} />

      <UserServicesModal
        open={servicesModalOpen}
        user={selectedUser}
        onClose={() => { setServicesModalOpen(false); setSelectedUser(null); }}
      />
    </Box>
  );
};

export default AdminUserList;
